import React, { KeyboardEvent, useState } from 'react';
import { Stack, FormControl, FormHelperText, InputLabel, Box, Tooltip } from '@mui/material';
import {
  AppointmentType,
  ExpandableField,
  ExpandableFieldItem,
  SchedulingData,
  AppointmentTypePicker,
} from '@features/appointment-types';
import { Patient } from '../../../../types/types';
import PatientPicker from '../../../../components/Patient/PatientPicker';
import { QuickPatientInfoPopover } from './QuickPatientInfoPopover';
import { capitalize } from '../../../../utils/utils';
import { useSubjectLanguage, useTemplatesList } from '@features/user';
import DurationSchedulePicker from './DurationSchedulePicker';
import RecurrencePicker from './RecurrencePicker';
import { CustomRecurrenceRule } from '../CustomRecurrenceModal';
import { ProviderPicker } from './ProviderPicker';
import { AppointmentNoteEditor } from './AppointmentNote';
import { LabelPicker, useAppointmentLabelsEnabled } from '@features/appointment-labels';
import { hasPatientContactMethod } from '@utils/patientUtils';
import { type ConcreteVisitModality, type VisitModality } from '@utils/modalityPlaceOfService';

import { SessionDetailsFields } from './SessionDetailsFields';

export interface AppointmentFormFieldsProps {
  // Patient
  selectedPatient?: Patient;
  onPatientSelect: (patient: Patient | undefined) => void;
  autoFocusPatientPicker?: boolean;

  // Appointment Type
  selectedAppointmentType?: AppointmentType;
  onAppointmentTypeSelect: (appointmentType: AppointmentType | undefined) => void;
  appointmentTypePickerNonDismissable?: boolean;

  // Duration Schedule (from useDurationSchedule)
  date: string;
  startTime: string;
  endTime: string;
  selectedDuration: number;
  isCustomDuration: boolean;
  onDateChange: (date: string) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onDurationSelect: (duration: number) => void;
  onCustomDurationSelect: () => void;

  // Recurrence (from useRecurrence)
  recurrence: string;
  customRecurrenceRule: CustomRecurrenceRule | null;
  onRecurrenceChange: (value: string) => void;
  onCustomRecurrenceClick: () => void;
  getRecurrenceText: () => string;

  // Telehealth (from useTelehealth)
  telehealth?: boolean;
  onTelehealthChange?: (checked: boolean) => void;

  // Session and billing overrides
  modality?: VisitModality;
  onModalityChange?: (modality: ConcreteVisitModality | null) => void;
  placeOfService?: string | null;
  onPlaceOfServiceChange?: (placeOfService: string | null) => void;
  cptCodes?: string[];
  onCptCodesChange?: (cptCodes: string[]) => void;
  useJotmeet?: boolean;
  onUseJotmeetChange?: (useJotmeet: boolean) => void;
  sessionError?: string | null;

  // Forms and Reminders (from useFormsAndReminders)
  selectedForms: ExpandableFieldItem[];
  selectedReminders: ExpandableFieldItem[];
  onAddForm: (formData: SchedulingData) => void;
  onUpdateForm: (formId: string | number, formData: SchedulingData) => void;
  onRemoveForm: (formId: string | number) => void;
  onAddReminder: (reminderData: SchedulingData) => void;
  onUpdateReminder: (reminderId: string | number, reminderData: SchedulingData) => void;
  onRemoveReminder: (reminderId: string | number) => void;

  // Provider (org schedule admin only)
  selectedProviderId?: string;
  onProviderSelect?: (providerId: string) => void;
  showProviderPicker?: boolean;
  providerIdForTypes?: string;

  // Mode-specific controls
  mode?: 'create' | 'edit' | 'view';
  showRecurrence?: boolean;

  // Action links - rendered between appointment type and schedule (only in view mode)
  actionLinks?: React.ReactNode;

  // Notes
  notes?: string;
  onNotesChange?: (notes: string) => void;

  // Labels (edit mode) — picker renders only when both are provided
  labelIds?: string[];
  onLabelIdsChange?: (labelIds: string[]) => void;
  organizationId?: string;

  // Keyboard event handling
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onKeyUp?: (event: KeyboardEvent<HTMLDivElement>) => void;
}

export const AppointmentFormFields: React.FC<AppointmentFormFieldsProps> = ({
  // Patient
  selectedPatient,
  onPatientSelect,
  autoFocusPatientPicker = false,

  // Appointment Type
  selectedAppointmentType,
  onAppointmentTypeSelect,
  appointmentTypePickerNonDismissable = false,

  // Duration Schedule
  date,
  startTime,
  endTime,
  selectedDuration,
  isCustomDuration,
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onDurationSelect,
  onCustomDurationSelect,

  // Recurrence
  recurrence,
  customRecurrenceRule,
  onRecurrenceChange,
  onCustomRecurrenceClick,
  getRecurrenceText,

  // Telehealth
  telehealth = false,
  onTelehealthChange,
  modality,
  onModalityChange,
  placeOfService,
  onPlaceOfServiceChange,
  cptCodes = [],
  onCptCodesChange,
  useJotmeet = false,
  onUseJotmeetChange,
  sessionError,

  // Forms and Reminders
  selectedForms,
  selectedReminders,
  onAddForm,
  onUpdateForm,
  onRemoveForm,
  onAddReminder,
  onUpdateReminder,
  onRemoveReminder,

  // Provider (org schedule admin)
  selectedProviderId,
  onProviderSelect,
  showProviderPicker = false,
  providerIdForTypes,

  // Mode-specific
  mode = 'create',
  showRecurrence = true,

  // Action links
  actionLinks,

  // Notes
  notes = '',
  onNotesChange,

  // Labels
  labelIds = [],
  onLabelIdsChange,
  organizationId,

  // Keyboard events
  onKeyDown,
  onKeyUp,
}) => {
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [popoverRequiredFields, setPopoverRequiredFields] = useState<
    Array<'phone' | 'dob' | 'email'>
  >([]);
  const subjectLanguage = useSubjectLanguage();
  const templatesList = useTemplatesList();
  const labelsEnabled = useAppointmentLabelsEnabled();

  const isFormsEnabled = (patient?: Patient): boolean => {
    return !!(patient?.dob && hasPatientContactMethod(patient));
  };

  const isRemindersEnabled = (patient?: Patient): boolean => {
    return hasPatientContactMethod(patient);
  };

  const getFormsDisabledText = (patient?: Patient): string => {
    const missingFields: string[] = [];
    if (!patient?.dob) missingFields.push('date of birth');
    if (!hasPatientContactMethod(patient)) missingFields.push('email or phone number');

    if (missingFields.length === 0) return '';

    return `${capitalize(subjectLanguage)} ${missingFields.join(' and ')} required to send forms`;
  };

  const getRemindersDisabledText = (patient?: Patient): string => {
    if (hasPatientContactMethod(patient)) return '';
    return 'Add an email or phone number to send reminders';
  };

  const handleEditPatientInfo = (
    requiredFields: Array<'phone' | 'dob' | 'email'>,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (selectedPatient?.patient_id) {
      setPopoverRequiredFields(requiredFields);
      setPopoverPosition({
        top: event.clientY,
        left: event.clientX,
      });
    }
  };

  const handleEditForForms = (event: React.MouseEvent<HTMLElement>) => {
    const missingFields: Array<'phone' | 'dob' | 'email'> = [];
    if (!selectedPatient?.dob) missingFields.push('dob');
    if (!hasPatientContactMethod(selectedPatient)) missingFields.push('phone', 'email');
    if (missingFields.length > 0) {
      handleEditPatientInfo(missingFields, event);
    }
  };

  const handleEditForReminders = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasPatientContactMethod(selectedPatient)) {
      handleEditPatientInfo(['phone', 'email'], event);
    }
  };

  return (
    <>
      <Stack spacing={3} onKeyDown={onKeyDown} onKeyUp={onKeyUp}>
        {/* Provider picker (org schedule admin only) */}
        {showProviderPicker && onProviderSelect && (
          <ProviderPicker value={selectedProviderId} onChange={onProviderSelect} />
        )}

        {/* Patient picker */}
        <PatientPicker
          onPatientSelect={onPatientSelect}
          initialPatient={selectedPatient}
          autoFocus={autoFocusPatientPicker}
        />

        {/* Appointment Type picker */}
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Tooltip
              slotProps={{
                popper: {
                  sx: { zIndex: 1300 },
                },
              }}
              title={
                selectedAppointmentType?.template_id
                  ? (() => {
                      const template = templatesList?.find(
                        t => t.template_id === selectedAppointmentType.template_id,
                      );
                      const templateName = template?.display_name || 'default';
                      return `Encounters created from this appointment type will default to the ${templateName} template.`;
                    })()
                  : ''
              }
              placement="top"
              arrow
            >
              <Box>
                <AppointmentTypePicker
                  onAppointmentTypeSelect={onAppointmentTypeSelect}
                  initialAppointmentType={selectedAppointmentType}
                  nonDismissable={appointmentTypePickerNonDismissable}
                  providerId={providerIdForTypes}
                />
              </Box>
            </Tooltip>
          </Box>
        </Stack>

        {onModalityChange && onPlaceOfServiceChange && onCptCodesChange ? (
          <FormControl variant="outlined" fullWidth error={Boolean(sessionError)}>
            <InputLabel
              shrink
              sx={{
                backgroundColor: 'background.paper',
                px: 0.5,
                transform: 'translate(14px, -9px) scale(0.75)',
              }}
            >
              Session
            </InputLabel>
            <Box
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                p: 2,
                pt: 3,
                '&:hover': { borderColor: 'text.primary' },
              }}
            >
              <SessionDetailsFields
                modality={modality}
                placeOfService={placeOfService}
                cptCodes={cptCodes}
                useJotmeet={useJotmeet}
                error={sessionError}
                onModalityChange={onModalityChange}
                onPlaceOfServiceChange={onPlaceOfServiceChange}
                onCptCodesChange={onCptCodesChange}
                onUseJotmeetChange={onUseJotmeetChange}
              />
            </Box>
          </FormControl>
        ) : onTelehealthChange ? (
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              shrink
              sx={{
                backgroundColor: 'background.paper',
                px: 0.5,
                transform: 'translate(14px, -9px) scale(0.75)',
              }}
            >
              Session
            </InputLabel>
            <Box
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                p: 2,
                pt: 3,
                '&:hover': { borderColor: 'text.primary' },
              }}
            >
              <SessionDetailsFields
                modality={telehealth ? 'video' : 'in_person'}
                placeOfService={null}
                cptCodes={[]}
                useJotmeet={false}
                disableBillingFields
                onModalityChange={nextModality => {
                  onTelehealthChange(nextModality === 'video' || nextModality === 'audio');
                }}
                onPlaceOfServiceChange={() => undefined}
                onCptCodesChange={() => undefined}
              />
            </Box>
          </FormControl>
        ) : null}

        {/* Action links - if passed in */}
        {actionLinks}

        {/* Schedule and duration - Using FormControl styling */}
        <FormControl variant="outlined" fullWidth>
          <InputLabel
            shrink
            sx={{
              backgroundColor: 'background.paper',
              px: 0.5,
              transform: 'translate(14px, -9px) scale(0.75)',
            }}
          >
            Date & Time
          </InputLabel>
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              p: 2,
              pt: 4,
              '&:hover': {
                borderColor: 'text.primary',
              },
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <DurationSchedulePicker
              date={date}
              startTime={startTime}
              endTime={endTime}
              selectedDuration={selectedDuration}
              isCustomDuration={isCustomDuration}
              onDateChange={onDateChange}
              onStartTimeChange={onStartTimeChange}
              onEndTimeChange={onEndTimeChange}
              onDurationSelect={onDurationSelect}
              onCustomDurationSelect={onCustomDurationSelect}
            />

            {/* Recurrence - conditionally shown */}
            {showRecurrence && (
              <RecurrencePicker
                recurrence={recurrence}
                customRecurrenceRule={customRecurrenceRule}
                onRecurrenceChange={onRecurrenceChange}
                onCustomRecurrenceClick={onCustomRecurrenceClick}
                text={getRecurrenceText()}
              />
            )}
          </Box>
        </FormControl>

        {/* Reminders */}
        <ExpandableField
          title="Reminders"
          items={selectedReminders}
          onAddItem={onAddReminder}
          onUpdateItem={onUpdateReminder}
          onDeleteItem={onRemoveReminder}
          isFormType={false}
          mode={mode}
          disabled={!isRemindersEnabled(selectedPatient)}
          onEditPatientInfo={selectedPatient ? handleEditForReminders : undefined}
          disabledText={getRemindersDisabledText(selectedPatient)}
        />

        {/* Forms */}
        <ExpandableField
          title="Forms"
          items={selectedForms}
          onAddItem={onAddForm}
          onUpdateItem={onUpdateForm}
          onDeleteItem={onRemoveForm}
          isFormType={true}
          mode={mode}
          disabled={!isFormsEnabled(selectedPatient)}
          onEditPatientInfo={selectedPatient ? handleEditForForms : undefined}
          disabledText={getFormsDisabledText(selectedPatient)}
        />

        {/* Labels (edit mode) */}
        {onLabelIdsChange && organizationId && labelsEnabled && (
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              shrink
              sx={{
                backgroundColor: 'background.paper',
                px: 0.5,
                transform: 'translate(14px, -9px) scale(0.75)',
              }}
            >
              Labels
            </InputLabel>
            <Box
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                p: 2,
                pt: 3,
                '&:hover': { borderColor: 'text.primary' },
              }}
            >
              <LabelPicker
                organizationId={organizationId}
                value={labelIds}
                onChange={onLabelIdsChange}
                trackingContext={{ scope: 'appointment', surface: 'appointment_sidebar' }}
              />
            </Box>
          </FormControl>
        )}

        {/* Notes */}
        {onNotesChange && (
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              shrink
              sx={{
                backgroundColor: 'background.paper',
                px: 0.5,
                transform: 'translate(14px, -9px) scale(0.75)',
              }}
            >
              Memos
            </InputLabel>
            <Box
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                overflow: 'hidden',
                '&:hover': { borderColor: 'text.primary' },
              }}
            >
              <AppointmentNoteEditor value={notes ?? ''} onChange={onNotesChange} />
            </Box>
            <FormHelperText>
              Not part of the clinical record — add notes, to-do items, or pre-visit instructions
              for the care team
            </FormHelperText>
          </FormControl>
        )}
      </Stack>

      {/* Quick Patient Info Popover */}
      {selectedPatient && (
        <QuickPatientInfoPopover
          anchorPosition={popoverPosition}
          open={Boolean(popoverPosition)}
          onClose={() => {
            setPopoverPosition(null);
            setPopoverRequiredFields([]);
          }}
          patient={selectedPatient}
          requiredFields={popoverRequiredFields}
          onSave={updates => {
            onPatientSelect({
              ...selectedPatient,
              ...updates,
            });
            setPopoverPosition(null);
            setPopoverRequiredFields([]);
          }}
        />
      )}
    </>
  );
};

export default AppointmentFormFields;
